import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../lib/api';
import ProductPage from '../components/ProductPage';
import { THEMES, isValidRealm } from '../lib/themes';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { trackProductView, trackAddToCart } from '../utils/analytics';

const ProductDetailPage = () => {
  const { realm, slug } = useParams();
  const navigate = useNavigate();
  const { addItem, openDrawer } = useCart();
  const { isAuthenticated } = useAuth();
  const { push: pushToast } = useToast();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const theme = realm && isValidRealm(realm) ? THEMES[realm] : null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await api.get(`/products/${slug}`);
        if (cancelled) return;
        if (res.data?.success) {
          setProduct(res.data.data);
          trackProductView(res.data.data._id, realm);
        } else {
          setError('Product not found');
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.message || 'Failed to load product');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, realm]);

  const handleAddToCart = (p, opts) => {
    addItem(p, opts);
    trackAddToCart(p._id, { name: p.name, price: p.price });
    pushToast(`Added ${p.name}`, { tone: 'success' });
    openDrawer();
  };

  const handleToggleWishlist = async (p) => {
    if (!isAuthenticated) {
      pushToast('Login to save favorites', { tone: 'info', duration: 2500 });
      return;
    }
    try {
      await api.post('/customer/wishlist', { productId: p._id });
      pushToast('Saved to wishlist', { tone: 'success' });
    } catch {
      pushToast('Could not update wishlist', { tone: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-3xl font-black uppercase animate-pulse">LOADING...</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-yellow-300">
        <div className="bg-white border-4 border-black p-8 max-w-md text-center shadow-[8px_8px_0px_black]">
          <h2 className="text-3xl font-black mb-2">PRODUCT NOT FOUND</h2>
          <p className="text-sm text-gray-600 mb-4">{error || 'Try going back to the catalog.'}</p>
          <button
            onClick={() => navigate(realm ? `/${realm}` : '/')}
            className="bg-black text-white px-6 py-3 font-black uppercase border-4 border-yellow-400 hover:bg-yellow-400 hover:text-black transition-colors"
          >
            ← BACK
          </button>
        </div>
      </div>
    );
  }

  return (
    <ProductPage
      product={product}
      theme={theme || THEMES[product.category] || null}
      onAddToCart={handleAddToCart}
      onToggleWishlist={handleToggleWishlist}
    />
  );
};

export default ProductDetailPage;
